import type { SetupOrigin, SetupSource } from '@mogzo/shared';
import { Link } from 'react-router';
import { timeAgo } from '../lib/format';

/** Whether the CLI (the developer, or an AI coding tool) made or last changed it (M18b). */
export const touchedByCli = (origin: SetupOrigin | null | undefined) =>
  origin?.madeFrom === 'cli' || origin?.changedFrom === 'cli';

export function TerminalIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 9l3 3-3 3" />
      <path d="M13 15h4" />
    </svg>
  );
}

/** The small tag in a list: made or changed from the CLI. */
export function CliTag() {
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-transparent bg-info-soft px-2 py-0.5 align-middle text-xs font-semibold whitespace-nowrap text-ink">
      <TerminalIcon />
      CLI
    </span>
  );
}

function Where({ from, on }: { from: SetupSource; on: string | null }) {
  if (from === 'dashboard') return <>here</>;
  return (
    <>
      from the CLI
      {on && (
        <>
          {' '}
          on{' '}
          <Link
            to="/cli/sessions"
            className="font-semibold underline decoration-2 underline-offset-2"
          >
            {on}
          </Link>
        </>
      )}
    </>
  );
}

/**
 * “Made from the CLI on Studio laptop · 3 min ago · changed here · just now”, shown when the
 * CLI made or changed it; the computer links to CLI sessions, where it can be signed out.
 */
export function MadeFrom({ origin }: { origin: SetupOrigin | null | undefined }) {
  if (!origin || !touchedByCli(origin)) return null;
  return (
    <p className="mt-2 flex flex-wrap items-center gap-1.5 rounded-[6px] border border-transparent bg-info-soft px-3 py-2 text-[13px] text-ink">
      <TerminalIcon />
      <span>
        Made <Where from={origin.madeFrom} on={origin.madeOn} /> · {timeAgo(origin.madeAt)}
        {origin.changedFrom && origin.changedAt && (
          <>
            {' '}
            · changed <Where from={origin.changedFrom} on={origin.changedOn} /> ·{' '}
            {timeAgo(origin.changedAt)}
          </>
        )}
      </span>
    </p>
  );
}
